import { useState } from "react";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { prettifyApiError } from "@/shared/api";
import { formatDateIt } from "@/shared/format";
import {
  type AuthUser,
  changeUserPassword,
  createUser,
  mfaReset,
  setUserActive,
} from "../auth/api";
import { useResource } from "./api";

/** Minimo password lato client (allineato al vincolo del backend web-mode). */
const MIN_PASSWORD = 10;

/**
 * Form aperto: creazione utente oppure cambio password di una riga.
 * Uno solo alla volta (aprirne uno chiude l'altro) — pattern inline del
 * resto delle Impostazioni, niente modal da gestire.
 */
type Editor =
  | { mode: "create"; username: string; password: string; confirm: string }
  | {
      mode: "password";
      userId: number;
      username: string;
      // Password ATTUALE di chi agisce (re-auth backend), non del target.
      currentPassword: string;
      password: string;
      confirm: string;
    };

function passwordIssue(password: string, confirm: string): string | null {
  if (password.length < MIN_PASSWORD) {
    return `La password deve avere almeno ${MIN_PASSWORD} caratteri.`;
  }
  if (password !== confirm) return "Le due password non coincidono.";
  return null;
}

// Formato condiviso: era la nona copia dello stesso Intl.DateTimeFormat.
const formatDate = formatDateIt;

const SMALL_BTN =
  "rounded-md border border-line bg-card px-3 py-1.5 text-[12px] text-ink-2 hover:border-accent " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft disabled:opacity-50";

const FIELD =
  "mt-1 h-11 w-full rounded-md border border-line bg-paper px-3 text-[13px] text-ink " +
  "focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft";

/**
 * Card "Utenti web" (Impostazioni → Account, SOLO web-mode — il gate
 * ``isWebMode()`` sta nel parent così sul desktop il componente non monta
 * e non parte nessuna fetch). Gestisce gli account del login applicativo:
 * lista, creazione, attiva/disattiva con conferma, cambio password.
 * Le password non vengono MAI mostrate (il backend espone solo metadata).
 */
export function UsersCard() {
  const users = useResource<AuthUser[]>("/api/auth/users", []);
  const me = useResource<{ user: AuthUser } | null>("/api/auth/me", null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<AuthUser | null>(null);
  // Conferma separata da `confirmToggle`: azzerare il 2FA revoca anche le
  // sessioni dell'utente, non è un'azione da fare per errore di mira.
  const [confirmMfa, setConfirmMfa] = useState<AuthUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  function resetFeedback() {
    setError(null);
    setMessage(null);
  }

  function openCreate() {
    setEditor({ mode: "create", username: "", password: "", confirm: "" });
    setConfirmToggle(null);
    setConfirmMfa(null);
    resetFeedback();
  }

  function openPassword(user: AuthUser) {
    setEditor({
      mode: "password",
      userId: user.id,
      username: user.username,
      currentPassword: "",
      password: "",
      confirm: "",
    });
    setConfirmToggle(null);
    setConfirmMfa(null);
    resetFeedback();
  }

  function openToggle(user: AuthUser) {
    setConfirmToggle(user);
    setEditor(null);
    setConfirmMfa(null);
    resetFeedback();
  }

  function openMfaReset(user: AuthUser) {
    setConfirmMfa(user);
    setEditor(null);
    setConfirmToggle(null);
    resetFeedback();
  }

  /** Esegue l'operazione con feedback uniforme + refresh lista. */
  async function run(op: () => Promise<void>, successMessage: string) {
    setBusy(true);
    resetFeedback();
    try {
      await op();
      setMessage(successMessage);
      await users.refresh();
    } catch (err) {
      // Include l'errore backend "ultimo utente attivo" sulla disattivazione.
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  function submitEditor(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editor || busy) return;
    const username = editor.username.trim();
    if (editor.mode === "create" && !username) {
      setError("Inserisci il nome utente.");
      return;
    }
    if (
      editor.mode === "password" &&
      editor.userId === me.value?.user.id &&
      !editor.currentPassword
    ) {
      setError("Inserisci la tua password attuale per confermare.");
      return;
    }
    const issue = passwordIssue(editor.password, editor.confirm);
    if (issue) {
      setError(issue);
      return;
    }
    void run(
      async () => {
        if (editor.mode === "create") await createUser(username, editor.password);
        else
          await changeUserPassword(editor.userId, editor.currentPassword || null, editor.password);
        setEditor(null);
      },
      editor.mode === "create"
        ? `Utente «${username}» creato.`
        : `Password di «${editor.username}» aggiornata.`,
    );
  }

  function submitToggle() {
    if (!confirmToggle || busy) return;
    const user = confirmToggle;
    void run(
      async () => {
        await setUserActive(user.id, !user.is_active);
        setConfirmToggle(null);
      },
      user.is_active
        ? `Utente «${user.username}» disattivato.`
        : `Utente «${user.username}» attivato.`,
    );
  }

  function submitMfaReset() {
    if (!confirmMfa || busy) return;
    const user = confirmMfa;
    void run(async () => {
      await mfaReset(user.id);
      setConfirmMfa(null);
      // Il pulsante sparisce solo dopo il refresh della lista (mfa_attiva
      // torna false): il feedback è la riga che cambia, non solo il messaggio.
    }, `Secondo fattore di «${user.username}» azzerato. Le sue sessioni sono state chiuse: al prossimo accesso lo riconfigura.`);
  }

  return (
    <div className="px-5 py-4 max-md:px-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">Utenti</h3>
          <p className="mt-0.5 text-[12px] text-ink-2">
            Account per l'accesso all'app pubblicata sul web. Le password non sono mai visibili: si
            possono solo impostare di nuovo.
          </p>
        </div>
        <button type="button" onClick={openCreate} disabled={busy} className={SMALL_BTN}>
          Nuovo utente
        </button>
      </div>

      {editor && (
        <form
          onSubmit={submitEditor}
          className="mb-3 space-y-3 rounded-lg border border-line bg-paper/60 p-3"
        >
          <div className="text-[13px] font-medium text-ink">
            {editor.mode === "create" ? "Nuovo utente" : `Cambia password — ${editor.username}`}
          </div>

          {editor.mode === "create" && (
            <label className="block text-[12px] font-medium text-ink-2">
              Nome utente
              <input
                type="text"
                value={editor.username}
                onChange={(event) =>
                  setEditor((prev) =>
                    prev?.mode === "create" ? { ...prev, username: event.target.value } : prev,
                  )
                }
                autoComplete="username"
                autoFocus
                className={FIELD}
              />
            </label>
          )}

          {editor.mode === "password" && editor.userId === me.value?.user.id && (
            <label className="block text-[12px] font-medium text-ink-2">
              La tua password attuale
              <input
                type="password"
                value={editor.currentPassword}
                onChange={(event) =>
                  setEditor((prev) =>
                    prev?.mode === "password"
                      ? { ...prev, currentPassword: event.target.value }
                      : prev,
                  )
                }
                autoComplete="current-password"
                autoFocus
                className={FIELD}
              />
            </label>
          )}

          <label className="block text-[12px] font-medium text-ink-2">
            {`Password (min ${MIN_PASSWORD} caratteri)`}
            <input
              type="password"
              value={editor.password}
              onChange={(event) =>
                setEditor((prev) => (prev ? { ...prev, password: event.target.value } : prev))
              }
              autoComplete="new-password"
              className={FIELD}
            />
          </label>

          <label className="block text-[12px] font-medium text-ink-2">
            Conferma password
            <input
              type="password"
              value={editor.confirm}
              onChange={(event) =>
                setEditor((prev) => (prev ? { ...prev, confirm: event.target.value } : prev))
              }
              autoComplete="new-password"
              className={FIELD}
            />
          </label>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditor(null)}
              disabled={busy}
              className={SMALL_BTN}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-full border border-accent bg-accent-2 px-3 py-1.5 text-[12px] font-medium text-white disabled:opacity-50"
            >
              {busy ? "Salvataggio…" : editor.mode === "create" ? "Crea utente" : "Salva password"}
            </button>
          </div>
        </form>
      )}

      <ErrorBanner message={error} className="mb-3" />
      {message && !error && (
        <div
          role="status"
          className="mb-3 rounded-full border border-green-soft bg-green-soft px-3 py-2 text-[12.5px] text-ink"
        >
          {message}
        </div>
      )}
      <ErrorBanner message={users.error} className="mb-3" />

      {users.isLoading && users.value.length === 0 ? (
        <p className="py-3 text-[13px] italic text-ink-2">Caricamento utenti…</p>
      ) : users.value.length === 0 ? (
        <p className="py-3 text-[13px] italic text-ink-2">Nessun utente configurato.</p>
      ) : (
        users.value.map((user) => (
          <div key={user.id} className="border-t border-dashed border-line-2 py-3 first:border-t-0">
            <div className="flex flex-wrap items-center gap-3">
              <div className="min-w-[160px] flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13.5px] font-medium text-ink">{user.username}</span>
                  <span className="rounded-full bg-paper-2 px-2 py-0.5 tabular-nums text-[12px] font-bold text-ink-2">
                    {user.role}
                  </span>
                </div>
                <div className="mt-0.5 tabular-nums text-[11px] text-ink-2">
                  Creato il {formatDate(user.created_at)}
                </div>
              </div>
              <span
                className={[
                  "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                  user.is_active ? "bg-green-soft text-ink" : "bg-paper-2 text-ink-2",
                ].join(" ")}
              >
                {user.is_active ? "Attivo" : "Disattivato"}
              </span>
              <button
                type="button"
                onClick={() => openPassword(user)}
                disabled={busy}
                className={SMALL_BTN}
              >
                Cambia password
              </button>
              <button
                type="button"
                onClick={() => openToggle(user)}
                disabled={busy}
                className={SMALL_BTN}
              >
                {user.is_active ? "Disattiva" : "Attiva"}
              </button>
              {/* Solo se il secondo fattore c'è davvero: il pulsante non deve
                  offrire di azzerare qualcosa che non esiste. */}
              {user.mfa_attiva && (
                <button
                  type="button"
                  onClick={() => openMfaReset(user)}
                  disabled={busy}
                  className={SMALL_BTN}
                  title="Telefono perso o cambiato: toglie il secondo fattore e chiude le sessioni aperte"
                >
                  Azzera 2FA
                </button>
              )}
            </div>

            {confirmToggle?.id === user.id && (
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-soft bg-amber-soft px-3 py-2">
                <span className="text-[12.5px] text-ink">
                  {user.is_active
                    ? `Disattivare l'accesso di «${user.username}»? Non potrà più fare login.`
                    : `Riattivare l'accesso di «${user.username}»?`}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmToggle(null)}
                    disabled={busy}
                    className={SMALL_BTN}
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={submitToggle}
                    disabled={busy}
                    className="rounded-full border border-amber bg-card px-3 py-1.5 text-[12px] font-medium text-amber hover:brightness-95 disabled:opacity-50"
                  >
                    {busy ? "Applico…" : "Conferma"}
                  </button>
                </div>
              </div>
            )}

            {confirmMfa?.id === user.id && (
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-soft bg-amber-soft px-3 py-2">
                <span className="text-[12.5px] text-ink">
                  {`Azzerare il secondo fattore di «${user.username}»? Le sue sessioni aperte vengono chiuse e al prossimo accesso dovrà riconfigurarlo.`}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmMfa(null)}
                    disabled={busy}
                    className={SMALL_BTN}
                  >
                    Annulla
                  </button>
                  <button
                    type="button"
                    onClick={submitMfaReset}
                    disabled={busy}
                    className="rounded-full border border-amber bg-card px-3 py-1.5 text-[12px] font-medium text-amber hover:brightness-95 disabled:opacity-50"
                  >
                    {busy ? "Azzero…" : "Conferma"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
